import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components/native';

import { Octicons } from '@expo/vector-icons';
import { WhereClause } from '@sierralabs/query-core';

import ActivityIndicatorScreen from '../../../../components/ActivityIndicatorScreen';
import AlertBox from '../../../../components/AlertBox';
import BarChart from '../../../../components/charts/BarChart';
import IconButton from '../../../../components/IconButton';
import Spacer from '../../../../components/Spacer';
import { H4, H5 } from '../../../../components/Typography';
import {
  StudyParticipantQueryParamsDto,
  useQueryStudyParticipantData
} from '../../../../hooks/study-participant/useQueryStudyParticipantData';
import WebViewModal from '../../../modals/WebViewModal';
import {
  ScreenComponentPlugin,
  ScreenComponentProps
} from '../ScreenComponentPlugin';
import Card from '../../../../components/Card';

const StyledView = styled(Card)`
  width: 100%;
  min-height: 96px;
  padding: 0 0 19px 0;
`;

const StyledIconButton = styled(IconButton)`
  margin-left: auto;
`;

const StyledNoDataText = styled(H5)`
  font-size: 21px;
  line-height: 24px;
  align-self: center;
  text-align: center;
  width: 60%;
`;

const ChartContainer = styled.View`
  position: relative;
`;

const NoDataOverlay = styled.View`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding-top: 20%;
  justify-content: start;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 1;
`;

const StyledTitleContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  padding: 19px 19px 0 19px;
`;

export interface BeforeAfterChartCardProps {
  title?: string;
  hasMoreInfo?: boolean;
  infoTitle?: string;
  infoHtml?: string;
  yAxisLabel?: string;
  entityId?: string;
  columnName?: string;
  beforeLabel?: string;
  beforeFilter?: WhereClause[];
  afterLabel?: string;
  afterFilter?: WhereClause[];
}

export const BeforeAfterChartCard: React.FC<
  ScreenComponentProps<BeforeAfterChartCardProps>
> = ({
  title,
  hasMoreInfo,
  infoTitle,
  infoHtml,
  yAxisLabel,
  entityId,
  columnName,
  beforeLabel,
  beforeFilter,
  afterLabel,
  afterFilter,
  studyParticipant,
  ...props
}) => {
  // Get before data
  const {
    queryResults: beforeResults,
    loadingQuery: loadingBefore,
    loadingQueryError: loadingBeforeError,
    queryData: getBeforeData
  } = useQueryStudyParticipantData<any>(studyParticipant?.id || 0);

  // Get after data
  const {
    queryResults: afterResults,
    loadingQuery: loadingAfter,
    loadingQueryError: loadingAfterError,
    queryData: getAfterData
  } = useQueryStudyParticipantData<any>(studyParticipant?.id || 0);

  useEffect(() => {
    if (studyParticipant?.id) {
      const beforeParams: StudyParticipantQueryParamsDto[] = [];
      const afterParams: StudyParticipantQueryParamsDto[] = [];

      if (entityId && columnName) {
        beforeParams.push({
          entityId: entityId,
          columnNames: [columnName],
          wheres: beforeFilter
        });

        afterParams.push({
          entityId: entityId,
          columnNames: [columnName],
          wheres: afterFilter
        });
      }

      if (beforeParams.length > 0) {
        getBeforeData(beforeParams);
      }
      if (afterParams.length > 0) {
        getAfterData(afterParams);
      }
    }
  }, [
    getBeforeData,
    getAfterData,
    studyParticipant?.id,
    entityId,
    columnName,
    beforeFilter,
    afterFilter
  ]);

  // Calculate averages for before and after periods
  const chartData = useMemo(() => {
    if (!studyParticipant?.id) {
      return [
        {
          x: beforeLabel || 'Before',
          y: 5.1,
          color: '#5b4fc2'
        },
        {
          x: afterLabel || 'After',
          y: 7.1,
          color: '#1c9c7d'
        }
      ];
    }

    let beforeAverage: number | null = null;
    let afterAverage: number | null = null;

    if (beforeResults && columnName) {
      const beforeValues = beforeResults
        .flat()
        .filter(row => {
          const value = row[columnName as keyof typeof row];
          return (
            value !== null &&
            value !== undefined &&
            value !== '' &&
            !isNaN(Number(value))
          );
        })
        .map(row => Number(row[columnName as keyof typeof row]));

      beforeAverage =
        beforeValues.length > 0
          ? beforeValues.reduce((sum, val) => sum + val, 0) /
            beforeValues.length
          : null;
    }

    if (afterResults && columnName) {
      const afterValues = afterResults
        .flat()
        .filter(row => {
          const value = row[columnName as keyof typeof row];
          return (
            value !== null &&
            value !== undefined &&
            value !== '' &&
            !isNaN(Number(value))
          );
        })
        .map(row => Number(row[columnName as keyof typeof row]));

      afterAverage =
        afterValues.length > 0
          ? afterValues.reduce((sum, val) => sum + val, 0) / afterValues.length
          : null;
    }

    const chartData = [
      {
        x: beforeLabel || 'Before',
        y: beforeAverage ?? 0,
        color: '#5b4fc2',
        hasData: beforeAverage !== null
      },
      {
        x: afterLabel || 'After',
        y: afterAverage ?? 0,
        color: '#1c9c7d',
        hasData: afterAverage !== null
      }
    ];

    return chartData;
  }, [columnName, beforeResults, afterResults, beforeLabel, afterLabel]);

  const [showModal, setShowModal] = useState<boolean>();

  // Check for null data
  const hasNoData =
    studyParticipant?.id &&
    chartData.every(dataPoint => !(dataPoint as any).hasData);

  return (
    <StyledView>
      <StyledTitleContainer>
        {Boolean(title) && <H4>{title}</H4>}
        {hasMoreInfo && (
          <StyledIconButton onPress={() => setShowModal(true)}>
            <Octicons name='question' size={24} color='#494753' />
          </StyledIconButton>
        )}
      </StyledTitleContainer>
      <Spacer size={2} />

      <ChartContainer>
        <BarChart
          data={chartData}
          yAxisLabel={yAxisLabel || ''}
          height={250}
          yAxisLeftOffset={6}
        />
        {hasNoData && (
          <NoDataOverlay>
            <StyledNoDataText>
              No data available for this chart
            </StyledNoDataText>
          </NoDataOverlay>
        )}
      </ChartContainer>

      {(loadingBefore || loadingAfter) && <ActivityIndicatorScreen modal />}
      {(loadingBeforeError || loadingAfterError) && (
        <AlertBox fullWidth severity='error'>
          Chart error:{' '}
          {loadingBeforeError?.message || loadingAfterError?.message}
        </AlertBox>
      )}
      {showModal && (
        <WebViewModal
          contentPadding
          visible={showModal}
          title={infoTitle || ''}
          html={infoHtml || ''}
          onClose={() => setShowModal(false)}
        />
      )}
    </StyledView>
  );
};

export const BeforeAfterChartCardPlugin: ScreenComponentPlugin<BeforeAfterChartCardProps> =
  {
    name: 'Before After Chart Card',
    type: 'BeforeAfterChartCard',
    categories: ['study-progress', 'my-chloe'],
    component: BeforeAfterChartCard
  };
